import React from "react";
import PropTypes from "prop-types";
import injectSheet from "react-jss";
import classNames from "classnames";
export const SESSION_CAM_EXCLUDE = "sessioncamexclude";
export const SESSION_CAM_HIDE_TEXT = "sessioncamhidetext";

const INPUT_HORIZONTAL_PADDING = 12;
const WIDEST_NUMBER = 12.5;
const WIDEST_LETTER_WITH_CAPS = 16.5;
const getNumberWidth = characters =>
  characters * WIDEST_NUMBER + INPUT_HORIZONTAL_PADDING * 2;
const getLetterWidth = characters =>
  characters * WIDEST_LETTER_WITH_CAPS + INPUT_HORIZONTAL_PADDING * 2;

const style = ({ theme }) => ({
  root: {
    flexGrow: ({ maxCharacters }) => (maxCharacters ? null : 1),
    width: ({ maxCharacters, type }) =>
      maxCharacters
        ? type === "number"
          ? getNumberWidth(maxCharacters)
          : getLetterWidth(maxCharacters)
        : null,
    position: "relative",
    alignItems: "center"
  },
  error: {
    "& $input, & $display, & $suffix": {
      color: theme.colors.tertiary2,
      borderBottom: 0
    }
  },
  display: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    borderBottom: `2px solid #D3C3BC`,
    // borderBottom: ({ disabled }) =>
    //   `${theme.borderWidths.default} solid ${
    //     disabled ? theme.colors.primary5 : theme.colors.primary5
    //   }`,
    // boxShadow: ({ disabled }) =>
    //   disabled ? "none" : "inset 0 1px 1px 0 rgba(0, 0, 0, 0.2)",
    borderRadius: theme.borderRadius.sm,
    backgroundColor: theme.colors.white,
    transition: "all 0.1s ease-in-out"
  },
  displayFocused: {
    borderBottom: `2px solid ${theme.colors.secondary1}`,
    // borderColor: ({ disabled, readOnly }) =>
    //   disabled || readOnly ? theme.colors.senary4 : theme.colors.primary2, // It cant be focused and disabled simultaneously but needs this for weight reasons
    "& $suffix, & $prefix": {
      color: theme.colors.primary2,
      fill: theme.colors.primary2
    }
  },
  input: {
    flex: 1,
    minWidth: 0,
    appearance: "textfield",
    boxSizing: "border-box",
    paddingTop: 8,
    paddingBottom: 8,
    outline: 0,
    border: theme.colors.transparent,
    backgroundColor: theme.colors.transparent,
    lineHeight: theme.leading.copy,
    fontFamily: theme.fonts.serif,
    fontSize: theme.textSizes.base,
    color: ({ invalid, disabled }) =>
      invalid
        ? theme.colors.tertiary2
        : disabled
        ? theme.colors.senary3
        : theme.colors.senary1,
    "&::placeholder": {
      color: theme.colors.senary2
    },
    "&:focus, &:disabled": {
      "&::placeholder": {
        color: theme.colors.senary3
      }
    },
    "&::-webkit-inner-spin-button": {
      "-webkit-appearance": "none",
      margin: 0
    },
    "&::-webkit-outer-spin-button": {
      "-webkit-appearance": "none",
      margin: 0
    },
    "&::-ms-clear": {
      display: "none"
    }
  },
  fix: {
    position: "absolute",
    top: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    lineHeight: theme.leading.none,
    fontFamily: theme.fonts.serif,
    userSelect: "none",
    pointerEvents: "none",
    color: theme.colors.senary2,
    fill: theme.colors.senary2,
    fontWeight: theme.fontWeights.bold,
    transition: "all 0.1s ease-in-out"
  },
  suffix: {
    composes: "$fix",
    right: 12
  },
  prefix: {
    composes: "$fix",
    left: 12
  }
});

const getInputPadding = fixWidth => {
  return fixWidth > 0
    ? fixWidth + 2 * INPUT_HORIZONTAL_PADDING
    : INPUT_HORIZONTAL_PADDING;
};

class FormInput extends React.Component {
  constructor(props) {
    super(props);

    this.prefixRef = React.createRef();
    this.suffixRef = React.createRef();
    this.inputRef = React.createRef();
    this.state = {
      focused: false,
      prefixWidth: 0,
      suffixWidth: 0
    };
  }

  handleFocus = e => {
    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
    this.setState({ focused: true });
  };

  handleBlur = e => {
    if (this.props.onBlur) {
      this.props.onBlur(e);
    }
    this.setState({ focused: false });
  };

  setPrefixWidth() {
    this.setState({
      prefixWidth: this.prefixRef.current
        ? this.prefixRef.current.getBoundingClientRect().width
        : 0
    });
  }

  setSuffixWidth() {
    this.setState({
      suffixWidth: this.suffixRef.current
        ? this.suffixRef.current.getBoundingClientRect().width
        : 0
    });
  }

  componentDidMount() {
    this.setPrefixWidth();
    this.setSuffixWidth();
  }

  componentDidUpdate(prevProps) {
    if (this.props.prefix !== prevProps.prefix) {
      this.setPrefixWidth();
    } else if (this.props.suffix !== prevProps.suffix) {
      this.setSuffixWidth();
    }
  }

  render() {
    const {
      id,
      suffix,
      prefix,
      classes,
      invalid,
      forwardRef,
      sessionCamExclude,
      maxCharacters,
      formContext,
      ...rest
    } = this.props;
    if (this.inputRef.current) {
      if (invalid) {
        this.inputRef.current.setCustomValidity("invalid");
      } else if (!invalid) {
        this.inputRef.current.setCustomValidity("");
      }
    }
    return (
      <div className={classNames(classes.root, { [classes.error]: invalid })}>
        <div
          className={classNames(classes.display, {
            [classes.displayFocused]: this.state.focused
          })}
        >
          {prefix ? (
            <span className={classes.prefix} ref={this.prefixRef}>
              {prefix}
            </span>
          ) : null}
          <input
            {...rest}
            invalid={invalid}
            ref={this.inputRef}
            id={id}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            className={classNames(classes.input, {
              [SESSION_CAM_EXCLUDE]: sessionCamExclude
            })}
            style={{
              paddingLeft: getInputPadding(this.state.prefixWidth),
              paddingRight: getInputPadding(this.state.suffixWidth)
            }}
          />
          {suffix ? (
            <span className={classes.suffix} ref={this.suffixRef}>
              {suffix}
            </span>
          ) : null}
        </div>
      </div>
    );
  }
}

FormInput.defaultProps = {
  value: "",
  disabled: false,
  readOnly: false,
  invalid: false
};

FormInput.propTypes = {
  /** This is a controlled input, a value prop is required */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  /** The minimum value to accept for this input */
  id: PropTypes.string.isRequired,
  /** Component or text to show at the input start */
  prefix: PropTypes.node,
  /** Component or text to show at the input end */
  suffix: PropTypes.node,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  maxCharacters: PropTypes.number,
  /** Indicates if this field is invalid */
  invalid: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  /** Reference that gets passed down to the input */
  ref: PropTypes.func,
  /** Indicates if this field has been excluded from recording by sessioncam */
  sessionCamExclude: PropTypes.bool
};
const RefInput = React.forwardRef((props, ref) => (
  <FormInput {...props} forwardRef={ref} />
));
RefInput.displayName = "FormInput";
export default injectSheet(style, { inject: ["classes"] })(RefInput);
